// services/live-session.ts
//
// Single source of truth for the live host->join->play->leaderboard loop.
// Topology is bandwidth-lean (Blaze RTDB bills on GB downloaded — minimize fan-out):
//   1. Quiz blob downloaded ONCE at join (getQuiz); never re-broadcast per question.
//   2. Clients subscribe only to the tiny session delta (subscribeToSession); they
//      derive the current question locally by indexing the cached quiz.
//   3. Participant list is NOT fanned out to clients — admin-only, denormalized name.
//   4. Answers are per-user write paths nobody subscribes to (submitAnswer).
//   5. Leaderboard publishes top-N only, once per question (publishLeaderboard).
import { db } from '../firebase';
import {
  ref,
  get,
  set,
  update,
  onValue,
  serverTimestamp,
} from 'firebase/database';
import {
  Quiz,
  Question,
  SessionState,
  LeaderboardEntry,
  Participant,
  WindowBoard,
} from '../types/quiz.types';

const LEADERBOARD_TOP_N = 10;
// v1: flat. Speed bonus + server-side scoring come later. Exported so the
// phone's local "your score" tally always matches the published leaderboard.
export const POINTS_PER_CORRECT = 1000;

// --- paths (kept here so every caller agrees) -------------------------------
const paths = {
  quiz: (quizId: string) => `quizzes/${quizId}`,
  correctAnswers: (quizId: string) => `correctAnswers/${quizId}`,
  session: (code: string) => `sessions/${code}`,
  participants: (code: string) => `quizDetails/${code}/participants`,
  participant: (code: string, uid: string) => `quizDetails/${code}/participants/${uid}`,
  answers: (code: string) => `quizAnswers/${code}`,
  answer: (code: string, uid: string, idx: number) => `quizAnswers/${code}/${uid}/${idx}`,
  ranks: (code: string) => `leaderboards/${code}/ranks`,
  leaderboardMeta: (code: string) => `leaderboards/${code}/meta`,
  user: (uid: string) => `users/${uid}`,
  // Day/week windowed boards, scoped per host + show tag. windowScores is the
  // full per-window tally (host-only, read/written once per session end);
  // windowBoards is the public top-N snapshot (one-time gets only).
  windowScores: (hostUid: string, show: string, period: string) =>
    `windowScores/${hostUid}/${show}/${period}`,
  windowBoard: (hostUid: string, show: string, period: string) =>
    `windowBoards/${hostUid}/${show}/${period}`,
  windowBoards: (hostUid: string) => `windowBoards/${hostUid}`,
};

// --- server clock -------------------------------------------------------------
// questionStartTime is written with RTDB's serverTimestamp(), so every client
// must measure "now" on the SERVER's clock, not its own — otherwise device clock
// skew makes countdowns unfair (and can leave a phone answerable after the
// reveal). `.info/serverTimeOffset` is local SDK state: zero fan-out.
let serverOffsetMs = 0;
let clockStarted = false;
const ensureServerClock = () => {
  if (clockStarted) return;
  clockStarted = true;
  onValue(ref(db, '.info/serverTimeOffset'), (snap) => {
    serverOffsetMs = Number(snap.val()) || 0;
  });
};
export const serverNow = (): number => {
  ensureServerClock();
  return Date.now() + serverOffsetMs;
};

// --- legacy-tolerant normalizer ---------------------------------------------
// Older quizzes were stored as objects keyed q1/q2 with options keyed opt1/opt2.
// New quizzes are arrays. Normalize either shape to a Question[] so the runtime
// only ever deals with index-addressable arrays.
const toArray = <T>(val: unknown): T[] => {
  if (Array.isArray(val)) return val.filter((v) => v != null) as T[];
  if (val && typeof val === 'object') return Object.values(val as Record<string, T>);
  return [];
};

const normalizeQuestion = (raw: any): Question => ({
  questionText: raw?.questionText ?? '',
  questionImage: raw?.questionImage || undefined,
  questionVideo: raw?.questionVideo || undefined,
  options: toArray<any>(raw?.options).map((o) =>
    typeof o === 'string' ? o : o?.optionText ?? ''
  ),
  timer: Number(raw?.timer) || 30,
});

// Coerce a stored correct-answer to a 0-based option index. New quizzes store a
// number directly; legacy quizzes stored 1-based ids like "opt2" -> index 1.
const toCorrectIndex = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const m = v.match(/\d+/);
    return m ? Number(m[0]) - 1 : -1;
  }
  return -1;
};

// --- read side --------------------------------------------------------------

// Download the full quiz ONCE (questions + timers, never correct answers).
export const getQuiz = async (quizId: string): Promise<Quiz | null> => {
  const snap = await get(ref(db, paths.quiz(quizId)));
  if (!snap.exists()) return null;
  const raw = snap.val();
  return {
    title: raw.title,
    createdBy: raw.createdBy,
    questions: toArray<any>(raw.questions).map(normalizeQuestion),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
};

// Host-only: fetch the answer key as 0-based correct indices. Rules allow the
// read for admins and for presenters on quizzes they can host (their own +
// catalog) — the host client needs it for scoring + the reveal; the bot engine
// reads it so seeded players can be made controllably correct/wrong. Never
// call this from an audience client (rules deny the read anyway).
export const getCorrectAnswers = async (quizId: string): Promise<number[]> => {
  const snap = await get(ref(db, paths.correctAnswers(quizId)));
  return toArray<unknown>(snap.val()).map(toCorrectIndex);
};

// Subscribe to the tiny session delta. Returns an unsubscribe fn.
// NOTE: always return onValue's own unsubscribe — `off(ref)` would detach EVERY
// listener on the path, so one component unmounting would silently kill another
// component's live subscription to the same node.
export const subscribeToSession = (
  code: string,
  cb: (session: SessionState | null) => void
): (() => void) => {
  ensureServerClock(); // warm the offset before the first countdown tick
  return onValue(ref(db, paths.session(code)), (snap) =>
    cb(snap.exists() ? (snap.val() as SessionState) : null)
  );
};

// Subscribe to top-N leaderboard. Returns an unsubscribe fn.
export const subscribeToLeaderboard = (
  code: string,
  cb: (ranks: LeaderboardEntry[]) => void
): (() => void) =>
  onValue(ref(db, paths.ranks(code)), (snap) => cb(toArray<LeaderboardEntry>(snap.val())));

// Host-only (admin or the session's hostUid): live participant roster
// (denormalized {uid: {name}} — no N+1). Used by SessionManager (pills) and
// PresenterView (lobby social proof). Never subscribed by players, so the
// no-fan-out rule holds.
export const subscribeToParticipants = (
  code: string,
  cb: (participants: Array<{ uid: string } & Participant>) => void
): (() => void) =>
  onValue(ref(db, paths.participants(code)), (snap) => {
    const data = (snap.val() ?? {}) as Record<string, Participant>;
    cb(Object.entries(data).map(([uid, p]) => ({ uid, ...p })));
  });

// One-time read for the ended screen: how big was the field ("you were one of
// 87 players"). Written once per question by publishLeaderboard — a few bytes.
export const getLeaderboardMeta = async (
  code: string
): Promise<{ totalPlayers: number } | null> => {
  const snap = await get(ref(db, paths.leaderboardMeta(code)));
  return snap.exists() ? (snap.val() as { totalPlayers: number }) : null;
};

// --- audience writes --------------------------------------------------------

// Join: denormalize the display name (and avatar) so the admin never N+1
// fetches users/{uid}. The avatar is a curated emoji — a couple of bytes.
export const joinSession = async (
  code: string,
  uid: string,
  name: string,
  avatar?: string
): Promise<void> => {
  await set(ref(db, paths.participant(code, uid)), {
    name,
    ...(avatar ? { avatar } : {}),
  });
};

// Persist the player's chosen nickname (+ avatar) for future sessions —
// persistent identity, so the same name/face shows up on next week's board.
// Child-path update, allowed by the users/{uid} self-write rule.
export const saveDisplayName = async (
  uid: string,
  name: string,
  avatar?: string
): Promise<void> => {
  await update(ref(db, paths.user(uid)), {
    name,
    ...(avatar ? { avatar } : {}),
  });
};

// Per-user answer write. Value is just the chosen option index. Nobody subscribes
// to this path, so it never fans out.
export const submitAnswer = async (
  code: string,
  uid: string,
  questionIndex: number,
  optionIndex: number
): Promise<void> => {
  await set(ref(db, paths.answer(code, uid, questionIndex)), optionIndex);
};

// Host-only (rules deny audience reads of quizAnswers/{code}; admin or the
// session's hostUid may read): live view of who has answered which question,
// so the host can see "14 / 23 answered" mid-question. Only the host's client
// downloads this — players never do, so the lean topology holds. Payload is
// tiny (uid -> {idx: option} numbers).
export const subscribeToAnswerCounts = (
  code: string,
  cb: (answersByUser: Record<string, Record<string, number>>) => void
): (() => void) =>
  onValue(ref(db, paths.answers(code)), (snap) =>
    cb((snap.val() ?? {}) as Record<string, Record<string, number>>)
  );

// --- host controls (admin, or the presenter whose hostUid is on the session) -

// questionStartTime is serverTimestamp() — written on the SERVER's clock, read
// back against serverNow(). The host's device clock never enters the countdown,
// so every phone sees the same remaining time regardless of local skew.
export const startSession = async (code: string): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    status: 'in_progress',
    currentQuestionIndex: 0,
    questionStartTime: serverTimestamp(),
    startedAt: serverTimestamp(),
    answersOpen: false, // stream mode: revealed on the broadcast, locked until host opens
  });
};

export const advanceQuestion = async (
  code: string,
  nextIndex: number
): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    currentQuestionIndex: nextIndex,
    questionStartTime: serverTimestamp(),
    answersOpen: false, // each new question starts locked (stream mode)
    revealedAnswerIndex: null, // clear last question's reveal
    shoutout: null, // a shout-out never outlives its question
  });
};

// Host answer-window controls. Stream mode: open when the question is visibly
// live on the broadcast, lock for the reveal. App mode ignores answersOpen —
// UNLESS delayOptions is set, where open is the "options up, clock starts" beat
// (the refreshed questionStartTime is what makes the countdown start at open).
export const openAnswers = async (code: string): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    answersOpen: true,
    questionStartTime: serverTimestamp(),
    revealedAnswerIndex: null, // re-opening the same question retracts the reveal
  });
};

// Locking optionally publishes the correct option index in the same write — a
// few bytes on the session delta every client already subscribes to (zero new
// fan-out). The answer key itself (correctAnswers/{quizId}) stays admin-only;
// players only ever learn the index AFTER the window is shut, so it can't be
// used to cheat.
export const closeAnswers = async (
  code: string,
  revealIndex?: number
): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    answersOpen: false,
    revealedAnswerIndex: revealIndex ?? null,
  });
};

// APP MODE reveal: the on-phone timer already gates answering, so the host
// reveals explicitly once time is up (SessionManager shows the button at 0).
export const revealAnswer = async (code: string, index: number): Promise<void> => {
  await update(ref(db, paths.session(code)), { revealedAnswerIndex: index });
};

// Set or clear the broadcast URL on a live session. Hosts typically create the
// game first, then go live and paste the URL — so this is editable any time from
// SessionManager. A non-empty value puts the session in stream mode; clearing it
// (null removes the child) reverts to app mode. Clients re-render on the update.
export const setSessionYoutube = async (code: string, youtubeUrl: string): Promise<void> => {
  const url = youtubeUrl.trim();
  await update(ref(db, paths.session(code)), { youtubeUrl: url || null });
};

// PLAY OPTION: hide options everywhere until the host opens answers, so the
// presenter can read the question aloud before anyone can guess. Stored on the
// session delta (one boolean — bytes); false clears the child entirely.
export const setSessionDelayOptions = async (code: string, delay: boolean): Promise<void> => {
  await update(ref(db, paths.session(code)), { delayOptions: delay || null });
};

// Tag the session with a show slug ("fun-friday") so the day/week windowed
// boards keep separate champion lists per show format. Blank clears the tag
// (boards fall back to the 'main' bucket).
export const setSessionShow = async (code: string, show: string): Promise<void> => {
  await update(ref(db, paths.session(code)), { show: toShowSlug(show) || null });
};

// STREAM MODE fame lever: put a random player's name on the broadcast. A few
// bytes on the session delta every client already subscribes to — zero new
// fan-out. `at` is the server clock so every surface ages the moment out
// consistently; PresenterView flashes it, the named player's phone celebrates.
export const publishShoutout = async (
  code: string,
  player: { uid: string; name: string; avatar?: string }
): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    shoutout: {
      uid: player.uid,
      name: player.name,
      ...(player.avatar ? { avatar: player.avatar } : {}),
      at: serverTimestamp(),
    },
  });
};

export const endSession = async (code: string): Promise<void> => {
  await update(ref(db, paths.session(code)), {
    status: 'ended',
    endedAt: serverTimestamp(),
  });
};

// Tie-break by name so equal scores don't reshuffle between publishes — a
// board that jitters on every reveal reads as broken on stream.
const toTopN = (entries: LeaderboardEntry[]): LeaderboardEntry[] =>
  [...entries]
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    .slice(0, LEADERBOARD_TOP_N);

// Presenter-client scoring core: recompute cumulative scores from the per-user
// answer writes. Idempotent (recomputes from scratch). Host-only reads.
//
// NOTE: this trusts the presenter's client. Acceptable for first validation only;
// move to a Cloud Function (Blaze) before anything paid/competitive.
const computeSessionScores = async (
  code: string,
  quizId: string
): Promise<{ entries: LeaderboardEntry[]; totalPlayers: number }> => {
  const [answersSnap, correctSnap, participantsSnap] = await Promise.all([
    get(ref(db, paths.answers(code))),
    get(ref(db, paths.correctAnswers(quizId))),
    get(ref(db, paths.participants(code))),
  ]);

  const participants = (participantsSnap.val() ?? {}) as Record<string, Participant>;
  const totalPlayers = Object.keys(participants).length;

  if (!answersSnap.exists()) return { entries: [], totalPlayers };

  const correct = toArray<unknown>(correctSnap.val()).map(toCorrectIndex);
  const answersByUser = answersSnap.val() as Record<string, Record<string, number>>;

  // If the answer key is missing or has no valid correct indices, every score is
  // 0 — the usual cause of a "scores but all zero" board. Surface it loudly with
  // the actual data so it's diagnosable from the console instead of a mystery.
  if (correct.length === 0 || !correct.some((c) => c >= 0)) {
    console.warn(
      `[publishLeaderboard] answer key for quiz ${quizId} is missing or has no correct ` +
        `indices — everyone scores 0. Recreate the quiz with "isCorrect": true on one ` +
        `option per question, and confirm you're signed in as admin (rules).`
    );
  }
  console.debug('[publishLeaderboard]', { quizId, correct, answers: answersByUser });

  const entries: LeaderboardEntry[] = Object.entries(answersByUser).map(
    ([uid, byIndex]) => {
      let score = 0;
      for (const [idx, chosen] of Object.entries(byIndex)) {
        const key = correct[Number(idx)];
        if (key >= 0 && key === Number(chosen)) score += POINTS_PER_CORRECT;
      }
      const p = participants[uid];
      return {
        uid,
        name: p?.name ?? 'Player',
        ...(p?.avatar ? { avatar: p.avatar } : {}),
        score,
      };
    }
  );
  return { entries, totalPlayers };
};

// Publish the top-N + the tiny meta ({totalPlayers}) — the only score fan-out
// clients ever see. Called on every advance/lock and on end.
export const publishLeaderboard = async (
  code: string,
  quizId: string
): Promise<void> => {
  const { entries, totalPlayers } = await computeSessionScores(code, quizId);
  await set(ref(db, paths.leaderboardMeta(code)), { totalPlayers });
  await set(ref(db, paths.ranks(code)), toTopN(entries));
};

// --- day/week windowed boards -------------------------------------------------
// "Manufacture fresh winners": scores also accumulate into per-day and per-week
// tallies scoped by host + show tag, so every day/week starts a new race. The
// full tally (windowScores) is host-only and touched once per session end; the
// public artifact is a top-N snapshot (windowBoards) players fetch with a
// one-time get — no subscriptions, so the bandwidth-lean rules hold.

// Show tags become RTDB-key-safe slugs; blank falls back to 'main'.
export const toShowSlug = (show?: string): string =>
  (show ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

// Window keys are derived on the SERVER clock in IST — the audience's "today"
// (shows run IST evenings; a UTC boundary would split a late show across days).
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
export const currentPeriods = (nowMs: number = serverNow()): { day: string; week: string } => {
  const ist = new Date(nowMs + IST_OFFSET_MS); // read via getUTC* = IST wall clock
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `d-${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
  // ISO-8601 week number (week contains that date's Thursday).
  const thu = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()));
  thu.setUTCDate(thu.getUTCDate() + 4 - (thu.getUTCDay() || 7));
  const jan1 = new Date(Date.UTC(thu.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((thu.getTime() - jan1.getTime()) / 86400000 + 1) / 7);
  const week = `w-${thu.getUTCFullYear()}-${pad(weekNo)}`;
  return { day, week };
};

// Fold this session's final scores into the host's day + week tallies and
// republish each window's top-N. Read-modify-write on a host-only path; runs
// exactly once, at session end, on the host's client.
const aggregateWindowBoards = async (
  hostUid: string,
  show: string | undefined,
  entries: LeaderboardEntry[]
): Promise<void> => {
  const slug = toShowSlug(show) || 'main';
  const { day, week } = currentPeriods();
  for (const period of [day, week]) {
    const scoresRef = ref(db, paths.windowScores(hostUid, slug, period));
    const snap = await get(scoresRef);
    const tally = (snap.val() ?? {}) as Record<
      string,
      { name: string; avatar?: string; score: number }
    >;
    for (const e of entries) {
      tally[e.uid] = {
        name: e.name, // latest nickname wins
        ...(e.avatar ? { avatar: e.avatar } : {}),
        score: (tally[e.uid]?.score ?? 0) + e.score,
      };
    }
    await set(scoresRef, tally);
    const ranked = Object.entries(tally).map(([uid, v]) => ({ uid, ...v }));
    const board: WindowBoard = {
      ranks: toTopN(ranked),
      totalPlayers: ranked.length,
      updatedAt: serverNow(),
    };
    await set(ref(db, paths.windowBoard(hostUid, slug, period)), board);
  }
};

// End-of-game pipeline: score once, publish the final board, fold the scores
// into the windowed boards, then flip the session to ended. Window aggregation
// is best-effort — a failure there must never leave the session stuck live.
export const finalizeSession = async (
  code: string,
  quizId: string,
  hostUid?: string,
  show?: string
): Promise<void> => {
  const { entries, totalPlayers } = await computeSessionScores(code, quizId);
  await set(ref(db, paths.leaderboardMeta(code)), { totalPlayers });
  await set(ref(db, paths.ranks(code)), toTopN(entries));
  if (hostUid && entries.length > 0) {
    try {
      await aggregateWindowBoards(hostUid, show, entries);
    } catch (err) {
      console.warn('[finalizeSession] window-board aggregation failed', err);
    }
  }
  await endSession(code);
};

// PUBLIC one-time read: every windowed board for a host (tiny — top-N lists),
// filtered to the current day/week by callers via currentPeriods(). Used by
// PresenterPage ("this week's champions") — never subscribed.
export const getWindowBoards = async (
  hostUid: string
): Promise<Record<string, Record<string, WindowBoard>>> => {
  const snap = await get(ref(db, paths.windowBoards(hostUid)));
  return (snap.val() ?? {}) as Record<string, Record<string, WindowBoard>>;
};
